import React from 'react'
import AddIcon from '@mui/icons-material/Add'
import { AppBar, Box, Button, Container } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import UserProfile from '@core/components/User/Profile'
import { routeMap } from '@core/constants/routes'
import { HEADER_HEIGHT } from '@core/constants/styles'
import { useMainState } from '@core/main-state'
import { useAppDispatch } from '@core/store'
import { useGetDefaultAuthorCreateRoute } from '@pages/author-v2/author-v2-hooks'
import { actions, authorClear } from '@pages/author/author-slice'
import { useBatches } from '@pages/author/batches/batches-state'

import FTGTooltip from '../FTGTooltip'
import Logo from './logo'
import Tabs from './tabs'

const disableCreateButtonInRoutes: string[] = [
  routeMap.AUTHOR_ROUTE,
  routeMap.AUTHOR_V2_LANDING_PAGE,
  routeMap.AUTHOR_V2_CREATE,
]

const pagesWithoutShadow: string[] = [
  routeMap.AUTHOR_V2_CREATE,
  routeMap.AUTHOR_V2_RECENT_WORK_BASE,
]

const CreateButton = () => {
  const dispatch = useAppDispatch()
  const defaultCreateLink = useGetDefaultAuthorCreateRoute()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation('common', { keyPrefix: 'header' })
  const createButton = useMainState((s) => s.createButton)

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()

    if (createButton.locationState) {
      navigate(createButton.nextLink!, { state: createButton.locationState })
      return
    }

    if (createButton.legacyAction) {
      dispatch(createButton.legacyAction)
      dispatch(actions.set({ selectedTab: 0 }))
      useBatches.setState({ selectedBatchId: null })
      navigate(createButton.nextLink!)
      return
    }

    dispatch(actions.set({ selectedTab: 0 }))
    useBatches.setState({ selectedBatchId: null })
    navigate(defaultCreateLink)
  }

  return (
    <FTGTooltip
      title={t('get_new_items')}
      disabled={location.pathname === '/author'}
      placement="right"
    >
      <div>
        <Button
          size="small"
          color="secondary"
          variant="contained"
          to={defaultCreateLink}
          component={Link}
          onClick={handleClick}
          disabled={disableCreateButtonInRoutes.includes(location.pathname)}
          aria-label={t('generate_new_items')}
          sx={{
            display: { xs: 'none', md: 'flex' },
          }}
        >
          <AddIcon sx={{ mr: 0.5, fontSize: 20 }} />
          {t('create')}
        </Button>
      </div>
    </FTGTooltip>
  )
}

const Header = () => {
  const dispatch = useAppDispatch()
  const [hasCustomer] = useMainState((state) => [Boolean(state.customer)])

  const location = useLocation()
  const hideShadow = pagesWithoutShadow.some((p) => location.pathname.startsWith(p))

  const handleClear = () => {
    dispatch(authorClear())
    useBatches.setState({ selectedBatchId: null })
  }

  return (
    <>
      <AppBar
        component="div"
        sx={{
          bgcolor: 'common.white',
          boxShadow: hideShadow
            ? 'none'
            : '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px 0 rgba(9, 30, 66, 0.31)',
          height: HEADER_HEIGHT,
          minWidth: '100%',
        }}
      >
        <Container
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          maxWidth="xl"
        >
          <Logo onClear={handleClear} />

          <Box mr="auto" display="flex" alignItems="center">
            {hasCustomer && <Tabs onClear={handleClear} />}
          </Box>

          <Box
            sx={{
              width: 390,
              justifyContent: { xs: 'flex-end', md: hasCustomer ? 'space-between' : 'flex-end' },
              alignItems: 'center',
              display: 'flex',
            }}
          >
            {hasCustomer && <CreateButton />}

            <UserProfile />
          </Box>
        </Container>
      </AppBar>
      <Box height={HEADER_HEIGHT} />
    </>
  )
}

export default Header
